/**
 * Gestione delle categorie merceologiche (classificazioni con sistema='merceologica').
 *
 * CRUD base sulla gerarchia categorie → sottocategorie, usando gli endpoint
 * /api/v1/classificazioni del core. Il conteggio articoli per nodo arriva
 * dall'endpoint /api/v1/classificazioni/tree che già filtra per UO corrente.
 *
 * Regola 6.8: importa solo da @floraltech/ui.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface TreeNode {
  id: string;
  codice: string;
  descrizione: string;
  parent_id: string | null;
  count_articoli: number;
  count_articoli_totale: number;
  children: TreeNode[];
}

const SISTEMA = 'merceologica';

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const codiceDa = (descrizione: string, parentCodice: string | null): string => {
  const clean = descrizione.trim().toUpperCase().slice(0, 40);
  if (parentCodice) return `${parentCodice}/${clean}`.slice(0, 50);
  return clean;
};

const CategorieManager: React.FC = () => {
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newRoot, setNewRoot] = useState('');
  const [newChildByParent, setNewChildByParent] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDescr, setEditDescr] = useState('');

  const fetchTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`/api/v1/classificazioni/tree?sistema=${SISTEMA}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      setTree((await resp.json()) as TreeNode[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTree();
  }, [fetchTree]);

  const createRoot = async () => {
    const nome = newRoot.trim();
    if (!nome) return;
    setError(null);
    try {
      const resp = await fetch('/api/v1/classificazioni', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          sistema: SISTEMA,
          codice: codiceDa(nome, null),
          descrizione: nome,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json())?.detail ?? `Errore ${resp.status}`);
      setNewRoot('');
      await fetchTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione');
    }
  };

  const createChild = async (parent: TreeNode) => {
    const nome = (newChildByParent[parent.id] ?? '').trim();
    if (!nome) return;
    setError(null);
    try {
      const resp = await fetch('/api/v1/classificazioni', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          sistema: SISTEMA,
          codice: codiceDa(nome, parent.codice),
          descrizione: nome,
          parent_id: parent.id,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json())?.detail ?? `Errore ${resp.status}`);
      setNewChildByParent((prev) => ({ ...prev, [parent.id]: '' }));
      await fetchTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione');
    }
  };

  const startEdit = (node: TreeNode) => {
    setEditingId(node.id);
    setEditDescr(node.descrizione);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDescr('');
  };

  const saveEdit = async (id: string) => {
    const nuovo = editDescr.trim();
    if (!nuovo) return;
    setError(null);
    try {
      const resp = await fetch(`/api/v1/classificazioni/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ descrizione: nuovo }),
      });
      if (!resp.ok) throw new Error((await resp.json())?.detail ?? `Errore ${resp.status}`);
      cancelEdit();
      await fetchTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore modifica');
    }
  };

  const removeNode = async (node: TreeNode) => {
    if (node.count_articoli_totale > 0) {
      if (!confirm(
        `La categoria "${node.descrizione}" contiene ${node.count_articoli_totale} articoli. Eliminare comunque?`,
      )) {
        return;
      }
    } else if (!confirm(`Eliminare la categoria "${node.descrizione}"?`)) {
      return;
    }
    setError(null);
    try {
      const resp = await fetch(`/api/v1/classificazioni/${node.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!resp.ok && resp.status !== 204) {
        throw new Error((await resp.json())?.detail ?? `Errore ${resp.status}`);
      }
      await fetchTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  const renderChild = (sc: TreeNode) => {
    const isEditing = editingId === sc.id;
    return (
      <div
        key={sc.id}
        className="flex items-center gap-2 rounded border border-border/50 bg-muted/30 px-3 py-2"
      >
        {isEditing ? (
          <>
            <Input
              value={editDescr}
              onChange={(e) => setEditDescr(e.target.value)}
              className="h-8 max-w-xs"
            />
            <Button size="sm" onClick={() => saveEdit(sc.id)}>
              Salva
            </Button>
            <Button size="sm" variant="ghost" onClick={cancelEdit}>
              Annulla
            </Button>
          </>
        ) : (
          <>
            <span className="flex-1 text-sm">{sc.descrizione}</span>
            <Badge variant="default" className="text-xs">
              {sc.count_articoli_totale}
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => startEdit(sc)}>
              Modifica
            </Button>
            <Button size="sm" variant="ghost" onClick={() => removeNode(sc)}>
              Elimina
            </Button>
          </>
        )}
      </div>
    );
  };

  const renderCategoria = (cat: TreeNode) => {
    const isEditing = editingId === cat.id;
    return (
      <Card key={cat.id}>
        <CardHeader className="flex flex-row items-center justify-between">
          {isEditing ? (
            <div className="flex flex-1 items-center gap-2">
              <Input
                value={editDescr}
                onChange={(e) => setEditDescr(e.target.value)}
                className="max-w-sm"
              />
              <Button size="sm" onClick={() => saveEdit(cat.id)}>
                Salva
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelEdit}>
                Annulla
              </Button>
            </div>
          ) : (
            <>
              <CardTitle className="text-lg">{cat.descrizione}</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="default">
                  {cat.count_articoli_totale} articoli
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => startEdit(cat)}>
                  Modifica
                </Button>
                <Button size="sm" variant="ghost" onClick={() => removeNode(cat)}>
                  Elimina
                </Button>
              </div>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {cat.children.length === 0 && (
            <p className="text-xs text-muted-foreground">Nessuna sottocategoria.</p>
          )}
          {cat.children.map(renderChild)}
          <div className="flex items-center gap-2 pt-2">
            <Input
              placeholder="Nuova sottocategoria..."
              value={newChildByParent[cat.id] ?? ''}
              onChange={(e) =>
                setNewChildByParent((prev) => ({ ...prev, [cat.id]: e.target.value }))
              }
              className="max-w-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void createChild(cat);
                }
              }}
            />
            <Button size="sm" onClick={() => createChild(cat)}>
              Aggiungi
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Categorie merceologiche</h1>
        <p className="text-sm text-muted-foreground">
          Le categorie sono globali per il tenant. Il conteggio articoli riflette il ramo corrente.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">Errore: {error}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nuova categoria root</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <Label htmlFor="new-root" className="sr-only">
            Nuova categoria
          </Label>
          <Input
            id="new-root"
            placeholder="Nome categoria (es. TINTE)"
            value={newRoot}
            onChange={(e) => setNewRoot(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void createRoot();
              }
            }}
            className="max-w-sm"
          />
          <Button onClick={createRoot}>Crea categoria</Button>
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-muted-foreground">Caricamento...</p>}

      {!loading && tree.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">Nessuna categoria presente.</p>
      )}

      {!loading && tree.length > 0 && (
        <div className="space-y-4">{tree.map(renderCategoria)}</div>
      )}
    </div>
  );
};

export default CategorieManager;
